// ============================================================
// Promote Students Page
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, FormControl, InputLabel, Select, MenuItem, Typography, Grid, Card, CardContent, Divider, TextField, Checkbox } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { CheckCircle } from 'lucide-react';
import { FcBusinessman, FcBusinesswoman } from 'react-icons/fc';
import { PageHeader } from '@/components/navigation/PageHeader';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { usePageMeta } from '@/hooks/usePageMeta';
import { useSchool } from '@/hooks/useSchool';
import useStudentStore from '@/store/studentStore';
import { academicSessionsApi } from '@/api/academicSessions.api';
import { studentsApi } from '@/api/students.api';
import toast from 'react-hot-toast';

export function PromoteStudentsPage() {
  usePageMeta('Promote Students', [
    { label: 'Students', path: '/students' },
    { label: 'Promote' },
  ]);

  const { schoolId } = useSchool();
  const promoteStudents = useStudentStore((s) => s.promoteStudents);

  // --- Source Filters State ---
  const [sourceSession, setSourceSession] = useState('');
  const [sourceClass, setSourceClass] = useState('');
  const [sourceSection, setSourceSection] = useState('');

  // --- Target Filters State ---
  const [targetSession, setTargetSession] = useState('');
  const [targetClass, setTargetClass] = useState('');
  const [targetSection, setTargetSection] = useState('');
  const [promotionDate, setPromotionDate] = useState(new Date().toISOString().split('T')[0]);
  const [remarks, setRemarks] = useState('');

  // --- Master Data ---
  const [sessions, setSessions] = useState([]);
  const [classesData, setClassesData] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (schoolId) {
      Promise.all([
        academicSessionsApi.getAllBySchool(schoolId),
        academicSessionsApi.getCurrentBySchool(schoolId).catch(() => null),
      ]).then(([allData, currentData]) => {
        const loadedSessions = allData || [];
        setSessions(loadedSessions);
        const current = (currentData && currentData.id)
          ? currentData
          : (loadedSessions.find(s => s.is_current) || loadedSessions.find(s => s.status === 'ACTIVE') || loadedSessions[0]);
        if (current && current.id) {
          setSourceSession(current.id);
        }
      }).catch(console.error);

      studentsApi.getClassesSections().then(res => {
        setClassesData(res?.data || []);
      }).catch(console.error);
    }
  }, [schoolId]);

  // Target Sessions (Future session validation removed temporarily)
  const targetSessions = sessions;

  // --- Source Students Fetch ---
  const [sourceStudents, setSourceStudents] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchSourceStudents = useCallback(async () => {
    // Only load students when user has selected BOTH source class and source section
    if (!schoolId || !sourceSession || !sourceClass || !sourceSection) {
      setSourceStudents([]);
      return;
    }
    setLoading(true);
    try {
      const res = await studentsApi.getStudents(schoolId, 1, 100, {
        academic_session_id: sourceSession,
        class_id: sourceClass,
        section_id: sourceSection,
        student_status: 'ACTIVE',
        student_statuses: ['ACTIVE'],
        enrollment_status: 'ACTIVE',
        enrollment_statuses: ['ACTIVE'],
      });
      const items = res?.items || res?.data || (Array.isArray(res) ? res : []);
      // Filter out any non-active students so only active enrollment students can be promoted
      const activeOnly = items.filter((s) => {
        const studentSt = (s.student_status || s.status || '').toUpperCase();
        const enrollmentSt = (s.enrollment_status || s.enrollmentStatus || s.student_status || s.status || '').toUpperCase();
        return (studentSt === 'ACTIVE' || !s.student_status) && (enrollmentSt === 'ACTIVE' || !s.enrollment_status);
      });
      setSourceStudents(activeOnly);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  }, [schoolId, sourceSession, sourceClass, sourceSection]);

  useEffect(() => {
    fetchSourceStudents();
  }, [fetchSourceStudents]);

  // Derived options
  const sourceClassObj = classesData.find(c => c.id === sourceClass);
  const sourceSections = sourceClassObj?.sections || [];
  
  const targetClassObj = classesData.find(c => c.id === targetClass);
  const targetSections = targetClassObj?.sections || [];

  const handleSourceClassChange = (selectedClassId) => {
    setSourceClass(selectedClassId);
    setSourceSection('');
    setSelectedStudents([]);
  };

  const handlePromote = async () => {
    if (selectedStudents.length === 0) return toast.error('Please select at least one student to promote.');
    if (!targetSession) return toast.error('Please select a Target Session.');
    if (!targetClass) return toast.error('Please select a Target Class.');
    if (!targetSection) return toast.error('Please select a Target Section.');

    // Guarantee only active students are promoted
    const activeSelectedStudents = selectedStudents.filter((id) => {
      const student = sourceStudents.find((s) => (s.student_id || s.id) === id);
      if (!student) return false;
      const studentSt = (student.student_status || student.status || '').toUpperCase();
      const enrollmentSt = (student.enrollment_status || student.enrollmentStatus || '').toUpperCase();
      return (studentSt === 'ACTIVE' || !student.student_status) && (enrollmentSt === 'ACTIVE' || !student.enrollment_status);
    });

    if (activeSelectedStudents.length === 0) {
      return toast.error('Only active students can be promoted. Please select active students.');
    }

    const payload = {
      from_session_id: sourceSession,
      to_session_id: targetSession,
      promotion_date: promotionDate,
      remarks: remarks || "Promoted",
      students: activeSelectedStudents.map((id) => {
        const student = sourceStudents.find((s) => (s.student_id || s.id) === id);
        const parsedRoll = student?.roll_number ? parseInt(student.roll_number, 10) : null;
        return {
          student_id: id,
          class_id: targetClass,
          section_id: targetSection,
          roll_number: !isNaN(parsedRoll) && parsedRoll !== null ? parsedRoll : null,
        };
      })
    };

    setIsSubmitting(true);
    try {
      await promoteStudents(schoolId, payload);
      toast.success(`${activeSelectedStudents.length} students successfully promoted!`);
      setSelectedStudents([]);
      setSourceClass('');
      setSourceSection('');
      setTargetSession('');
      setTargetClass('');
      setTargetSection('');
      setRemarks('');
      fetchSourceStudents(); // Refresh the list
    } catch (err) {
      toast.error(err.message || 'Failed to promote students');
    } finally {
      setIsSubmitting(false);
    }
  };

  const columns = [
    {
      field: 'select',
      headerName: '',
      width: 52,
      minWidth: 52,
      maxWidth: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      resizable: false,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => {
        const activeStudents = sourceStudents.filter((s) => {
          const studentSt = (s.student_status || s.status || '').toUpperCase();
          const enrollmentSt = (s.enrollment_status || s.enrollmentStatus || '').toUpperCase();
          return (studentSt === 'ACTIVE' || !s.student_status) && (enrollmentSt === 'ACTIVE' || !s.enrollment_status);
        });
        const activeIds = activeStudents.map((s) => s.student_id || s.id);
        const allSelected = activeIds.length > 0 && activeIds.every((id) => selectedStudents.includes(id));
        const someSelected = activeIds.some((id) => selectedStudents.includes(id)) && !allSelected;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <Checkbox
              size="small"
              disabled={activeIds.length === 0}
              indeterminate={someSelected}
              checked={allSelected}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedStudents(activeIds);
                } else {
                  setSelectedStudents([]);
                }
              }}
              sx={{ p: 0.5 }}
            />
          </Box>
        );
      },
      renderCell: (params) => {
        const id = params.row.student_id || params.row.id;
        const studentSt = (params.row.student_status || params.row.status || '').toUpperCase();
        const enrollmentSt = (params.row.enrollment_status || params.row.enrollmentStatus || '').toUpperCase();
        const isActive = (studentSt === 'ACTIVE' || !params.row.student_status) && (enrollmentSt === 'ACTIVE' || !params.row.enrollment_status);
        const isChecked = selectedStudents.includes(id);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <Checkbox
              size="small"
              disabled={!isActive}
              checked={isChecked}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedStudents((prev) => [...prev, id]);
                } else {
                  setSelectedStudents((prev) => prev.filter((item) => item !== id));
                }
              }}
              sx={{ p: 0.5 }}
            />
          </Box>
        );
      },
    },
    { 
      field: 'student_name', 
      headerName: 'Student Name', 
      flex: 1.2,
      minWidth: 180,
      valueGetter: (params, row) => row?.student_name || '',
      renderCell: (params) => {
        const isFemale = String(params.row?.gender || '').toUpperCase() === 'FEMALE';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
            <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {isFemale ? <FcBusinesswoman size={26} /> : <FcBusinessman size={22} />}
            </Box>
            <Typography variant="body2" fontWeight={500}>{params.row?.student_name || '-'}</Typography>
          </Box>
        );
      },
    },
    { field: 'admission_no', headerName: 'Adm. No.', flex: 0.8, minWidth: 110, valueGetter: (val, row) => row?.admission_no || '-' },
    { field: 'roll_number', headerName: 'Roll No.', flex: 0.7, minWidth: 90, valueGetter: (val, row) => row?.roll_number || '-' },
    { 
      field: 'class_name', 
      headerName: 'Current Class', 
      flex: 0.8, 
      minWidth: 110,
      valueGetter: (val, row) => row?.class?.name || row?.class_name || row?.className || '-'
    },
    { 
      field: 'section_name', 
      headerName: 'Current Section', 
      flex: 0.8, 
      minWidth: 110,
      valueGetter: (val, row) => row?.section?.name || row?.section_name || row?.sectionName || '-'
    },
    { field: 'gender', headerName: 'Gender', flex: 0.7, minWidth: 90, valueGetter: (val, row) => row?.gender || '-' },
    { 
      field: 'father_name', 
      headerName: "Father's Name", 
      flex: 1, 
      minWidth: 140,
      valueGetter: (val, row) => row?.parent?.father_name || row?.father_name || '-',
      renderCell: (params) => params.row?.parent?.father_name || params.row?.father_name || '-'
    },
    { 
      field: 'father_mobile', 
      headerName: 'Parent Phone', 
      flex: 0.9, 
      minWidth: 120,
      valueGetter: (val, row) => row?.mobile || row?.parent?.father_mobile || row?.father_mobile || '-',
      renderCell: (params) => params.row?.mobile || params.row?.parent?.father_mobile || params.row?.father_mobile || '-'
    },
    {
      field: 'student_status',
      headerName: 'Student Status',
      flex: 0.8,
      minWidth: 125,
      valueGetter: (val, row) => row?.student_status || row?.status || 'ACTIVE',
      renderCell: (params) => {
        const rawStatus = params.row?.student_status || params.row?.status || 'ACTIVE';
        const st = String(rawStatus).toUpperCase();
        return (
          <StatusBadge 
            status={st === 'ACTIVE' ? 'success' : 'error'} 
            label={st} 
          />
        );
      },
    },
    {
      field: 'enrollment_status',
      headerName: 'Enrollment Status',
      flex: 0.9,
      minWidth: 135,
      valueGetter: (val, row) => row?.enrollment_status || 'ACTIVE',
      renderCell: (params) => {
        const rawStatus = params.row?.enrollment_status || 'ACTIVE';
        const st = String(rawStatus).toUpperCase();
        return (
          <StatusBadge 
            status={st === 'ACTIVE' ? 'success' : 'error'} 
            label={st} 
          />
        );
      },
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <PageHeader 
        title="Promote Students" 
        subtitle="Perform bulk promotions of students to a new academic session and class."
      />

      <Grid container spacing={3}>
        {/* Source Settings */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card elevation={0} sx={{ height: '100%' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} color="primary">
                1. Select Source (Students to Promote)
              </Typography>
              <Divider />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                  <InputLabel>Source Session *</InputLabel>
                  <Select
                    value={sourceSession}
                    label="Source Session *"
                    onChange={(e) => {
                      setSourceSession(e.target.value);
                      setSelectedStudents([]);
                    }}
                  >
                    {sessions.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.session_name} {s.is_current ? '(Current Active)' : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                  <InputLabel>Source Class *</InputLabel>
                  <Select
                    value={sourceClass}
                    label="Source Class *"
                    onChange={(e) => handleSourceClassChange(e.target.value)}
                  >
                    <MenuItem value="" disabled><em>Select Class</em></MenuItem>
                    {classesData.map((cls) => (
                      <MenuItem key={cls.id} value={cls.id}>{cls.class_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180, flex: 1 }} disabled={!sourceClass}>
                  <InputLabel>Source Section *</InputLabel>
                  <Select
                    value={sourceSection}
                    label="Source Section *"
                    onChange={(e) => {
                      setSourceSection(e.target.value);
                      setSelectedStudents([]);
                    }}
                  >
                    <MenuItem value="" disabled><em>Select Section</em></MenuItem>
                    {sourceSections.map((sec) => (
                      <MenuItem key={sec.id} value={sec.id}>{sec.section_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Target Settings */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card elevation={0} sx={{ height: '100%', border: '1px solid', borderColor: 'primary.main', bgcolor: 'primary.50' }}>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle1" fontWeight={600} color="primary.dark">
                2. Set Target Destination (Promote To)
              </Typography>
              <Divider sx={{ borderColor: 'primary.200' }} />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                  <InputLabel>Target Session *</InputLabel>
                  <Select
                    value={targetSession}
                    label="Target Session *"
                    onChange={(e) => setTargetSession(e.target.value)}
                  >
                    <MenuItem value="" disabled><em>Select Session</em></MenuItem>
                    {targetSessions.length === 0 ? (
                      <MenuItem value="" disabled>No sessions available</MenuItem>
                    ) : (
                      targetSessions.map((s) => (
                        <MenuItem key={s.id} value={s.id}>{s.session_name}</MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                  <InputLabel>Target Class *</InputLabel>
                  <Select
                    value={targetClass}
                    label="Target Class *"
                    onChange={(e) => {
                      setTargetClass(e.target.value);
                      setTargetSection('');
                    }}
                  >
                    <MenuItem value="" disabled><em>Select Class</em></MenuItem>
                    {classesData.map((cls) => (
                      <MenuItem key={cls.id} value={cls.id}>{cls.class_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180, flex: 1 }} disabled={!targetClass}>
                  <InputLabel>Target Section *</InputLabel>
                  <Select
                    value={targetSection}
                    label="Target Section *"
                    onChange={(e) => setTargetSection(e.target.value)}
                  >
                    <MenuItem value="" disabled><em>Select Section</em></MenuItem>
                    {targetSections.map((sec) => (
                      <MenuItem key={sec.id} value={sec.id}>{sec.section_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                <TextField 
                  size="small" 
                  label="Promotion Date" 
                  type="date" 
                  value={promotionDate}
                  onChange={(e) => setPromotionDate(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ width: 180 }}
                />
                <TextField 
                  size="small" 
                  label="Remarks (Optional)" 
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  sx={{ flex: 1 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Student List Grid */}
      <WidgetContainer title={`3. Select Students (${selectedStudents.length} selected)`} noPadding>
        <Box sx={{ width: '100%' }}>
          <DataGrid
            autoHeight
            rows={sourceStudents}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.student_id || row.id || row.admission_no}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 },
              },
            }}
            slots={{
              noRowsOverlay: () => (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', py: 6 }}>
                  <Typography variant="body2" fontWeight={500} color="text.secondary" align="center">
                    {!sourceClass || !sourceSection
                      ? 'Please select Source Class and Source Section to view students.'
                      : 'No active students found in this class & section.'}
                  </Typography>
                </Box>
              )
            }}
            sx={{
              border: 'none',
              minHeight: 260,
              '& .MuiDataGrid-columnHeaders': {
                bgcolor: 'background.neutral',
                borderRadius: 0,
              },
              '& .MuiDataGrid-columnHeader--alignCenter .MuiDataGrid-columnHeaderTitleContainer': {
                justifyContent: 'center',
                overflow: 'visible',
              },
              '& .MuiDataGrid-columnHeaderTitleContainerContent': {
                overflow: 'visible',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              },
              '& .MuiDataGrid-overlayWrapper': {
                minHeight: 200,
              },
              '& .MuiDataGrid-overlay': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              },
            }}
          />
        </Box>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CheckCircle size={18} />}
            onClick={handlePromote}
            disabled={isSubmitting || selectedStudents.length === 0 || !targetSession || !targetClass || !targetSection}
          >
            {isSubmitting ? 'Promoting...' : `Promote ${selectedStudents.length} Students`}
          </Button>
        </Box>
      </WidgetContainer>
    </Box>
  );
}
